<template>
  <v-card class="grow pt-5 pb-8 px-4">
    <div class="rounded overflow-hidden">
      <v-tabs
        background-color="primary"
        dark
        v-model="tab"
      >
        <v-tab
          v-for="t in availableTabs"
          :key="t.key"
          :to="t.key"
        >{{ t.title }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" v-if="allQuartilesDataLoaded">
        <v-tab-item
          v-for="t in availableTabs"
          :key="t.key"
          :id="t.key"
          :value="t.key"
        >

        </v-tab-item>
      </v-tabs-items>
    </div>
    <v-container
      v-if="!allQuartilesDataLoaded"
      class="d-flex fill-height full-width"
    >
      <platform-spinner
        :size="50"
        class="d-flex fill-height full-width mx-auto my-auto"
      />
    </v-container>
    <v-container v-else-if="benchmarksEnabled" class="d-flex fill-height justify-space-between">
      <v-row v-if="quartileGraphData">
        <v-col :cols="12" :xl="2"></v-col>
        <v-col :cols="12" :xl="8">
          <v-row>
            <v-col class="px-16 mx-16">
              <gauge-graph
                v-if="quartileGraphData"
                :data="quartileGraphData"
                :config="quartileGraphConfig"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="text-center">
              <h2>{{ topPercentileText }}</h2>
            </v-col>
          </v-row>

          <v-container fluid v-if="comparisonBenchmarkEnabled && comparisonQuartileGraphData">
            <v-row>
              <v-col :cols="6">
                <gauge-graph
                  :data="comparisonQuartileGraphData"
                  :config="comparisonQuartileGraphConfig"
                />
              </v-col>
              <v-col :cols="6" class="d-flex justify-center align-center pr-lg-16">
                <h3>{{ comparisonPercentileText }}</h3>
              </v-col>
            </v-row>
          </v-container>

          <v-row class="display-graph flex-column">
            <v-col :cols="12" class="d-flex justify-center align-center">
              <div class="d-inline-block pr-5">
                <span class="quartile-circle d-inline-block"/>
                <span class="text-no-wrap px-3">{{ title }}</span>
              </div>
              <div v-if="comparisonBenchmarkEnabled" class="d-inline-block pr-5">
                <span class="quartile-triangle d-inline-block"/>
                <span class="text-no-wrap px-3">{{ comparisonTitle }}</span>
              </div>
            </v-col>
            <v-col class="d-flex justify-center align-center" :class="{ 'text-no-wrap': $vuetify.breakpoint.lgAndUp }" :cols="12">
              <div class="d-inline-block pr-5">
                <span class="result-bubble small status-danger white--text d-inline-block px-3 py-2 mr-3"/>
                <span class="text-no-wrap">{{ $t('report.overallEffectiveness.legend.1stQuartile') }}</span>
              </div>
              <div class="d-inline-block pr-5">
                <span class="result-bubble small status-critical white--text d-inline-block px-3 py-2 mr-3"/>
                <span class="text-no-wrap">{{ $t('report.overallEffectiveness.legend.2ndQuartile') }}</span>
              </div>
              <div class="d-inline-block pr-5">
                <span class="result-bubble small status-warning white--text d-inline-block px-3 py-2 mr-3"/>
                <span class="text-no-wrap">{{ $t('report.overallEffectiveness.legend.3rdQuartile') }}</span>
              </div>
              <div class="d-inline-block pr-5">
                <span class="result-bubble small status-okay white--text d-inline-block px-3 py-2 mr-3"/>
                <span class="text-no-wrap">{{ $t('report.overallEffectiveness.legend.4thQuartile') }}</span>
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col :cols="12" :xl="2"></v-col>
      </v-row>
    </v-container>
    <v-container v-else class="pt-10">
      <h3>{{ noBenchmarksEnabledText }}</h3>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import { PlatformReport } from '@/types/Report'
import { ScoreCollection } from '@betterboards/shared/helpers/entities/Result/scores'
import { ReportBenchmark } from '@betterboards/shared/types/ReportBenchmark'
import {
  PlatformReportGroup,
  ReportConfiguration
} from '@betterboards/shared/types/Report'
import { ReportQuartiles } from '@betterboards/shared/types/Quartiles'
import { GaugeGraphConfig, GaugeGraphData } from '@betterboards/graphs/variants/GaugeGraph'
import GaugeGraph from '@/components/graphs/GaugeGraph.vue'
import { ResultSet } from '@betterboards/shared/types/ResultSet'
import { getQuartilesDescription } from '@betterboards/shared/helpers/statistics/quartiles/getQuartilesDescription'
import { fetchReportGroupBySurveyGroupId } from '@/helpers/reportGroup'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import { PlatformSurveyGroup } from '@betterboards/shared/types/SurveyGroup'
import {
  showBBOverallEffectivenessOption,
  showUniverseOverallEffectivenessOption
} from '@betterboards/shared/helpers/entities/Report/showOverallEffectiveness'
import { PlatformCompany } from '@betterboards/shared/types/Company'
import { TopPercentileBands } from '@betterboards/shared/helpers/statistics/index'
import checkTopPercentile from '@betterboards/shared/helpers/statistics/quartiles/checkTopPercentile'
import { $t } from '@betterboards/shared/translations/index'

enum OverallEffectivenessTab {
  System = 'system',
  Universe = 'universe',
  None = 'none'
}

const isValidQuartile = (quartile: any, benchmarkEnabled: boolean) => quartile && benchmarkEnabled

const DefaultGraphConfig: Partial<GaugeGraphConfig> = {
  width: 700,
  height: 450,
  scale: 1.2
}

interface QuartileTab {
  key: OverallEffectivenessTab
  title: string
}

export default Vue.extend({
  name: 'OverallEffectiveness',
  components: {
    GaugeGraph,
    PlatformSpinner
  },
  data: () => ({
    quartiles: <ReportQuartiles | undefined>undefined,
    quartileGraphData: <GaugeGraphData | undefined>undefined,
    quartileGraphConfig: <Partial<GaugeGraphConfig> | undefined>undefined,
    comparisonQuartiles: <ReportQuartiles | undefined>undefined,
    comparisonQuartileGraphData: <GaugeGraphData | undefined | null>undefined,
    comparisonQuartileGraphConfig: <Partial<GaugeGraphConfig> | undefined>undefined,
    comparisonReportGroup: <PlatformReportGroup | undefined>undefined,
    tab: <OverallEffectivenessTab>OverallEffectivenessTab.System
  }),
  async mounted (): Promise<void> {
    if (this.comparisonReportBenchmark?.enabled && !this.comparisonReportGroup) {
      await this.getComparisonReportGroup()
    }
    this.setActiveQuartileTab()
    await this.init()
  },
  watch: {
    'reportGroup.scores' (newVal, oldVal) {
      const hasNewComparisonScore: ReportBenchmark | undefined = newVal.some(
        (score) => score.collection === ScoreCollection.ComparisonAverage
      )
      const hadOldComparisonScore: ReportBenchmark | undefined = oldVal.some(
        (score) => score.collection === ScoreCollection.ComparisonAverage
      )

      if (hadOldComparisonScore && !hasNewComparisonScore) {
        this.comparisonReportGroup = undefined
        this.init()
      }
      if (hasNewComparisonScore) {
        this.init()
      }
    },
    tab () {
      this.setQuartiles()
      this.setComparisonQuartiles()
    },
    quartiles () {
      this.initializeQuartileGraphData()
    },
    comparisonQuartiles () {
      this.initializeComparisonQuartileGraphData()
    },
    availableTabs () {
      this.setActiveQuartileTab()
    },
    'reportGroup.quartiles': 'init',
    '$route.params.quartileData': 'setActiveQuartileTab'
  },
  computed: {
    company (): PlatformCompany {
      return this.$store.state.Company.selectedCompany
    },
    surveyGroup (): PlatformSurveyGroup | undefined {
      return this.$store.getters['Analysis/selectedSurveyGroup']
    },
    resultSet (): ResultSet | null {
      return this.$store.state.Analysis.resultSet
    },
    comparisonResultSet (): ResultSet | null {
      return this.$store.state.Analysis.comparisonResultSet
    },
    reportGroup (): PlatformReportGroup | null {
      return this.$store.getters['Report/selectedReportGroup']
    },
    selectedReport (): PlatformReport | null {
      return this.$store.getters['Report/selectedReport']
    },
    systemQuartile (): ReportQuartiles | undefined {
      const quartiles: ReportQuartiles | undefined = this.reportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.SystemAverage
      )
      const validBands: boolean | undefined = quartiles?.quartiles?.quartileBands?.every((band) => band.lower && band.upper)
      if (validBands) {
        return quartiles
      }

      console.warn('Invalid system quartile bands')
      return undefined
    },
    universeQuartile (): ReportQuartiles | undefined {
      const quartiles: ReportQuartiles | undefined = this.reportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.UniverseAverage
      )
      const validBands: boolean | undefined = quartiles?.quartiles?.quartileBands?.every((band) => band.lower && band.upper)
      if (validBands) {
        return quartiles
      }

      console.warn('Invalid universe quartile bands')
      return undefined
    },
    comparisonSystemQuartile (): ReportQuartiles | undefined {
      return this.comparisonReportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.SystemAverage
      )
    },
    comparisonUniverseQuartile (): ReportQuartiles | undefined {
      return this.comparisonReportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.UniverseAverage
      )
    },
    availableTabs (): QuartileTab[] {
      const tabs: QuartileTab[] = []
      if (!this.benchmarksEnabled) {
        tabs.push({
          key: OverallEffectivenessTab.None,
          title: 'Overall Effectiveness'
        })
      }
      if (this.systemQuartile && this.systemBenchmarkEnabled) {
        tabs.push({
          key: OverallEffectivenessTab.System,
          title: this.title ?? 'Better Boards'
        })
      }
      if (this.universeQuartile && this.universeBenchmarkEnabled) {
        tabs.push({
          key: OverallEffectivenessTab.Universe,
          title: this.universeTitle ?? 'Universe'
        })
      }

      return tabs
    },
    comparisonReportBenchmark (): ReportBenchmark | undefined {
      return this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.ComparisonAverage
      )
    },
    title (): string | undefined {
      if (!this.selectedReport) {
        return undefined
      }
      const reportConfiguration: ReportConfiguration | undefined = typeof this.selectedReport?.configuration === 'string' ? JSON.parse(this.selectedReport?.configuration) : this.selectedReport?.configuration
      return reportConfiguration?.sectionAverageTitle ?? this.company.name
    },
    universeTitle (): string | undefined {
      return this.universeQuartile?.title
    },
    comparisonTitle (): string | undefined {
      return this.comparisonReportBenchmark?.title
    },
    topPercentileText (): string | undefined {
      if (!this.resultSet || !this.quartiles) {
        return undefined
      }
      return getQuartilesDescription(this.resultSet, this.quartiles)
    },
    comparisonPercentileText (): string | undefined {
      if (!this.comparisonResultSet || !this.comparisonQuartiles) {
        return undefined
      }
      return getQuartilesDescription(this.comparisonResultSet, this.comparisonQuartiles, true)
    },
    allQuartilesDataLoaded (): boolean {
      if (!this.benchmarksEnabled) {
        return true
      }
      if (!this.comparisonReportGroup || !this.comparisonQuartiles) {
        return !!this.quartileGraphData
      }
      return !!this.quartileGraphData && !!this.comparisonQuartileGraphData
    },
    systemBenchmarkEnabled (): boolean {
      const systemBenchmark: ReportBenchmark | undefined = this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.SystemAverage
      )
      return !!systemBenchmark?.enabled && showBBOverallEffectivenessOption(this.surveyGroup?.featureConfiguration?.overallEffectiveness)
    },
    universeBenchmarkEnabled (): boolean {
      const universeBenchmark: ReportBenchmark | undefined = this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.UniverseAverage
      )
      return !!universeBenchmark?.enabled && showUniverseOverallEffectivenessOption(this.surveyGroup?.featureConfiguration?.overallEffectiveness)
    },
    comparisonBenchmarkEnabled (): boolean {
      const comparisonBenchmark: ReportBenchmark | undefined = this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.ComparisonAverage
      )
      return !!comparisonBenchmark?.enabled
    },
    benchmarksEnabled (): boolean {
      return this.systemBenchmarkEnabled || this.universeBenchmarkEnabled
    },
    noBenchmarksEnabledText (): string {
      return this.$t('overallEffectiveness.noBenchmarks') as string
    }
  },
  methods: {
    async init (): Promise<void> {
      this.setQuartiles()
      this.initializeQuartileGraphData()

      if (this.comparisonReportBenchmark?.enabled && !this.comparisonReportGroup) {
        await this.getComparisonReportGroup()
      }
      this.setComparisonQuartiles()
      this.initializeComparisonQuartileGraphData()
    },
    initializeQuartileGraphData (): void {
      if (!this.quartiles) {
        console.error('No Quartiles data found')
        this.quartileGraphData = undefined
        return
      }

      this.quartileGraphData = {
        overallScore: this.quartiles.quartiles.overallScore,
        comparisonOverallScore: this.quartiles.quartiles.comparisonOverallScore,
        quartileBands: this.quartiles.quartiles.quartileBands,
        tags: this.quartiles?.quartiles.tags
      }

      const topPercentile: TopPercentileBands | undefined = checkTopPercentile(
        this.quartiles.quartiles.overallScore,
        this.quartiles.quartiles.quartileBands[0].lower,
        this.quartiles.quartiles.quartileBands[this.quartiles.quartiles.quartileBands.length - 1].upper
      )
      const topPercentileText: string | undefined = topPercentile
        ? $t(`report.overallEffectiveness.topPercentileBands.${topPercentile}`) as string
        : undefined

      this.quartileGraphConfig = {
        ...DefaultGraphConfig,
        topPercentile: topPercentileText
      }
    },
    initializeComparisonQuartileGraphData (): void {
      if (!this.comparisonQuartiles) {
        console.warn('No comparison Quartiles data found')
        this.comparisonQuartileGraphData = undefined
        return
      }

      this.comparisonQuartileGraphData = {
        overallScore: this.comparisonQuartiles?.quartiles.overallScore,
        comparisonOverallScore: this.comparisonQuartiles?.quartiles.comparisonOverallScore,
        quartileBands: this.comparisonQuartiles?.quartiles.quartileBands,
        tags: this.comparisonQuartiles?.quartiles.tags
      }

      this.comparisonQuartileGraphConfig = {
        ...DefaultGraphConfig,
        comparisonOnly: true
      }
    },
    setQuartiles (): void {
      const tabQuartilesMap = {
        [OverallEffectivenessTab.System]: {
          quartileData: this.systemQuartile,
          benchmarkEnabled: this.systemBenchmarkEnabled
        },
        [OverallEffectivenessTab.Universe]: {
          quartileData: this.universeQuartile,
          benchmarkEnabled: this.universeBenchmarkEnabled
        }
      }

      const quartileTab = tabQuartilesMap[this.tab]
      if (quartileTab) {
        const { quartileData, benchmarkEnabled } = quartileTab

        if (!isValidQuartile(quartileData, benchmarkEnabled)) {
          this.quartiles = undefined
          if (this.tab === OverallEffectivenessTab.Universe) {
            this.tab = OverallEffectivenessTab.System
          }
          return
        }

        this.quartiles = {
          ...quartileData,
          quartiles: {
            ...quartileData.quartiles,
            overallScore: this.resultSet?.questionnaireScore ?? 0,
            comparisonOverallScore: this.comparisonResultSet?.questionnaireScore ?? (this.tab === OverallEffectivenessTab.Universe ? 0 : undefined)
          }
        }
      }

      return undefined
    },
    setComparisonQuartiles (): void {
      const tabComparisonQuartilesMap = {
        [OverallEffectivenessTab.System]: {
          quartileData: this.comparisonSystemQuartile,
          benchmarkEnabled: this.comparisonBenchmarkEnabled
        },
        [OverallEffectivenessTab.Universe]: {
          quartileData: this.comparisonUniverseQuartile,
          benchmarkEnabled: this.comparisonBenchmarkEnabled
        }
      }

      const comparisonQuartileTab = tabComparisonQuartilesMap[this.tab]
      if (comparisonQuartileTab) {
        const { quartileData, benchmarkEnabled } = comparisonQuartileTab
        if (!isValidQuartile(quartileData, benchmarkEnabled)) {
          this.comparisonQuartiles = undefined
          return
        }

        this.comparisonQuartiles = {
          ...quartileData,
          quartiles: {
            ...quartileData.quartiles,
            overallScore: this.comparisonResultSet?.questionnaireScore ?? 0
          }
        }
      }

      return undefined
    },
    async getComparisonReportGroup (): Promise<void> {
      if (!this.comparisonResultSet) {
        console.error('No comparison result set found')
        return
      }
      const surveyGroupId: string | undefined = this.comparisonResultSet.id
      const comparisonReportGroup: PlatformReportGroup | undefined = await fetchReportGroupBySurveyGroupId(
        surveyGroupId,
        this.comparisonResultSet.company.id
      )
      if (!comparisonReportGroup) {
        console.error('Comparison is missing report groups')
        return
      }

      this.comparisonReportGroup = {
        ...comparisonReportGroup,
        scores: typeof comparisonReportGroup.scores === 'string' ? JSON.parse(comparisonReportGroup.scores) : comparisonReportGroup.scores
      }
    },
    setActiveQuartileTab (): void {
      if (this.$route.params.quartileData) {
        const tabIndex = this.availableTabs.findIndex((tab) => tab.key === this.$route.params.quartileData)
        if (tabIndex !== -1 && this.tab !== this.availableTabs[tabIndex].key) {
          this.tab = this.availableTabs[tabIndex].key
        }
      }

      if (this.$route.name === 'AnalysisOverallEffectiveness') {
        if (!this.$route.params.quartileData || !this.availableTabs.some((t) => t.key === this.$route.params.quartileData)) {
          this.$router.replace({
            params: {
              quartileData: this.availableTabs[0].key
            }
          })
        }
      }
    }
  }
})
</script>
